<script setup lang="ts">
import { type Component, computed, ref } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";
import { useRouter } from "vue-router";

import { createNewAgent } from "~/components/agent/createAndOpenAgent";
import Avatar from "~/components/dumb/Avatar.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import MultiselectDropdownMenu from "~/components/dumb/MultiselectDropdownMenu.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import UserDropdownItem from "~/components/dumb/UserDropdownItem.vue";
import { LEFTBAR_SECTION_ACTION_CLASSES } from "~/components/leftbar/constants";
import { USER_KIND_CUSTOM } from "~/constants/userKinds";
import { AddAgentIcon, PlusIcon, UsersInviteIcon } from "~/icons";
import { makeLinkToSettingsRef } from "~/router/common";
import { Placement, UserRole, ViewMode } from "~/shared/enums";
import type { MultiselectDropdownMenuItem, MultiselectDropdownMenuSection, User } from "~/shared/types";
import { useDataStore, useUserStore } from "~/stores";
import { getUserDisplayName, isAgentInactive, isAgentSetupRequired } from "~/utils/user";

const props = withDefaults(
  defineProps<{
    agentsOnly?: boolean;
    container?: string;
    close?: () => void;
    mode?: "default" | "leftbar" | "header";
  }>(),
  {
    container: undefined,
    close: undefined,
    mode: "default",
  }
);

const router = useRouter();
const dataStore = useDataStore();
const userStore = useUserStore();

const menu = ref<ComponentExposed<typeof MultiselectDropdownMenu> | null>(null);

const isBuildMode = computed(() => userStore.viewMode === ViewMode.BUILD);

const inactiveAgents = computed(() =>
  dataStore.getUserList({ excludeNonagents: true, includeInactiveAgents: true }).filter(isAgentInactive)
);

const generateItemForAgent = (user: User): MultiselectDropdownMenuItem => ({
  value: user.duid,
  label: getUserDisplayName(user),
  adtlSearchTerms: [user.email],
  selected: false,
  component: UserDropdownItem,
  componentArgs: {
    replaceEnabled: false,
    icon: Avatar,
    iconArgs: { user, class: "icon-lg" },
    user,
    redirectEnabled: true,
  },
});

const generateItemForConfig = (value: string, label: string, icon: Component): MultiselectDropdownMenuItem => ({
  value,
  label,
  selected: false,
  component: UserDropdownItem,
  componentArgs: {
    replaceEnabled: false,
    icon,
    iconArgs: { class: "icon-lg text-lt" },
    redirectEnabled: false,
  },
});

const inactiveAgentItems = computed(() =>
  (isBuildMode.value
    ? dataStore
        .getBuildAgentList({ includeInactive: true, includeUnavailable: true })
        .filter((agent: User) => isAgentInactive(agent) || isAgentSetupRequired(agent))
    : inactiveAgents.value
  ).map(generateItemForAgent)
);

const createAgentItems = computed<MultiselectDropdownMenuItem[]>(() => [
  generateItemForConfig(USER_KIND_CUSTOM, "Create an agent", AddAgentIcon),
]);

const sections = computed<MultiselectDropdownMenuSection[]>(() => [
  { title: "Create an agent", showTitle: false, items: createAgentItems.value },
  ...(inactiveAgentItems.value.length > 0 ? [{ title: "", showTitle: false, items: inactiveAgentItems.value }] : []),
]);

const onClick = () => {
  if (inactiveAgentItems.value.length > 0) {
    return;
  }
  props.close?.();
  createNewAgent();
};

const onAdd = (value: string | boolean | number | null) => {
  if (typeof value !== "string") {
    return;
  }
  props.close?.();
  if (dataStore.getUserByDuid(value)) {
    router.push({ name: "agent", params: { agentDuid: value } });
    return;
  }
  createNewAgent();
};

const inviteTeammate = () => {
  props.close?.();
  router.push(makeLinkToSettingsRef("teammates").value);
};

defineExpose({
  isOpen: computed(() => !!menu.value?.isOpen),
});
</script>

<template>
  <div v-if="userStore.isRoleGte(UserRole.MEMBER)" :class="mode === 'default' && 'border-t border-md pt-1'">
    <MultiselectDropdownMenu
      ref="menu"
      :container="container"
      :sections="sections"
      :disabled="inactiveAgentItems.length === 0"
      header-title="Set up an agent"
      placeholder="Search agents..."
      :placement="Placement.RIGHT_TOP"
      :cover="false"
      :distance="2"
      :width-pixels="240"
      show-all
      close-on-select
      block
      @add="onAdd">
      <button
        v-if="mode === 'leftbar'"
        type="button"
        class="group/add-agent-trigger flex h-7 w-full cursor-pointer items-center gap-2 rounded-sm pr-2.5 pl-3.5 text-left text-sm font-medium text-vlt focus-ring-lt transition-colors drag-none hover:bg-md hover:text-lt"
        data-testid="setup-agent-leftbar-trigger"
        @click="onClick">
        <AddAgentIcon class="icon-sm shrink-0 text-vlt group-hover/add-agent-trigger:text-lt" />
        <span class="min-w-0 flex-1 truncate">Set up an agent</span>
      </button>
      <Tooltip v-else-if="mode === 'header'" text="Set up an agent">
        <button
          type="button"
          :data-on="menu?.isOpen"
          :class="LEFTBAR_SECTION_ACTION_CLASSES"
          aria-label="Add an agent"
          @click="onClick">
          <PlusIcon class="icon-sm text-lt" />
        </button>
      </Tooltip>
      <DropdownMenuItemContent
        v-else
        :icon="AddAgentIcon"
        title="Set up an agent"
        :is-submenu="inactiveAgentItems.length > 0"
        class="cursor-pointer hover:bg-md"
        @click="onClick" />
    </MultiselectDropdownMenu>
    <DropdownMenuItemContent
      v-if="!agentsOnly"
      :icon="UsersInviteIcon"
      title="Invite a teammate"
      class="cursor-pointer hover:bg-md"
      @click="inviteTeammate" />
  </div>
</template>
